import { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from '@omnitok/ui';
import { Download, Maximize2, Minimize2 } from 'lucide-react';

interface JsonViewerProps {
  value: unknown;
  fileName: string;
  onDownload?: () => void | Promise<void>;
}

function isContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === 'object';
}

function entries(value: Record<string, unknown> | unknown[]): Array<[string, unknown]> {
  return Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);
}

function collectContainerPaths(value: unknown, path: string, paths: Set<string>): void {
  if (!isContainer(value)) return;
  paths.add(path);
  for (const [key, child] of entries(value)) collectContainerPaths(child, `${path}.${key}`, paths);
}

function initialPaths(value: unknown): Set<string> {
  const paths = new Set<string>();
  if (isContainer(value)) {
    paths.add('$');
    for (const [key, child] of entries(value)) {
      if (isContainer(child)) paths.add(`$.${key}`);
    }
  }
  return paths;
}

function scalar(value: unknown) {
  if (value === null) return <span className="json-scalar json-scalar--null">null</span>;
  if (typeof value === 'string') return <span className="json-scalar json-scalar--string">&quot;{value}&quot;</span>;
  if (typeof value === 'number') return <span className="json-scalar json-scalar--number">{String(value)}</span>;
  if (typeof value === 'boolean') return <span className="json-scalar json-scalar--boolean">{String(value)}</span>;
  return <span className="json-scalar">{String(value)}</span>;
}

function JsonNode({
  value,
  nodeKey,
  path,
  depth,
  expanded,
  onToggle,
}: {
  value: unknown;
  nodeKey?: string;
  path: string;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
}) {
  const container = isContainer(value);
  const open = expanded.has(path);
  const children = container ? entries(value) : [];
  const summary = Array.isArray(value) ? `[${children.length}]` : `{${children.length}}`;

  return (
    <div className={depth ? 'json-node json-node--nested' : 'json-node'}>
      <div className="json-node__line">
        {container ? (
          <button
            type="button"
            aria-label={open ? 'Contraer nodo' : 'Abrir nodo'}
            onClick={() => onToggle(path)}
            className="json-node__toggle"
          >
            {open ? '−' : '+'}
          </button>
        ) : <span className="json-node__toggle" aria-hidden="true" />}
        {nodeKey !== undefined && <strong className="json-node__key">{nodeKey}:</strong>}
        {container ? (
          <span className="json-node__container">{open ? (Array.isArray(value) ? '[' : '{') : summary}</span>
        ) : scalar(value)}
      </div>
      {container && open && (
        <div>
          {children.map(([key, child]) => (
            <JsonNode
              key={`${path}.${key}`}
              value={child}
              nodeKey={key}
              path={`${path}.${key}`}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
          <span className="json-node__closing">{Array.isArray(value) ? ']' : '}'}</span>
        </div>
      )}
    </div>
  );
}

export default function JsonViewer({ value, fileName, onDownload }: JsonViewerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => initialPaths(value));
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const allPaths = useMemo(() => {
    const paths = new Set<string>();
    collectContainerPaths(value, '$', paths);
    return paths;
  }, [value]);

  useEffect(() => setExpanded(initialPaths(value)), [value]);

  const download = async () => {
    setDownloadError(null);
    try {
      if (onDownload) {
        await onDownload();
        return;
      }
      const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'No fue posible descargar el JSON.');
    }
  };

  return (
    <div className="json-viewer">
      <div className="json-viewer__actions">
        <Button size="xs" variant="neutral" ghost leftIcon={<Maximize2 size={14} />} onClick={() => setExpanded(new Set(allPaths))}>
          Expandir todo
        </Button>
        <Button size="xs" variant="neutral" ghost leftIcon={<Minimize2 size={14} />} onClick={() => setExpanded(new Set())}>
          Contraer todo
        </Button>
        <Button size="xs" variant="outline" leftIcon={<Download size={14} />} onClick={() => { void download(); }}>
          Descargar JSON
        </Button>
      </div>
      {downloadError && <Alert variant="error">{downloadError}</Alert>}
      <div className="json-viewer__content">
        <JsonNode
          value={value}
          path="$"
          depth={0}
          expanded={expanded}
          onToggle={(path) => setExpanded((current) => {
            const next = new Set(current);
            if (next.has(path)) next.delete(path); else next.add(path);
            return next;
          })}
        />
      </div>
    </div>
  );
}
